import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { queryClient } from '@/api/queryClient';
import { useNotificationsStore } from './notifications';
import { enableOfflineCache } from '@/app/offlineCache';

export type AuthUser = {
  id: string;
  username: string;
  /** Backend `users.account` — the chosen login handle. Distinct from
   *  `username` (which can be system-generated). The Mine page shows this
   *  next to the avatar; falls back to `username` for sessions persisted
   *  before this field existed. */
  account?: string;
  avatar?: string;
};

export type AuthSessionSource = 'password' | 'telegram-oauth' | 'telegram-miniapp';

/**
 * Why a session ended.
 *
 * `signed-out` covers every deliberate end — the user pressing sign out, a
 * password change, dismissing the session-kicked prompt. `unauthorized` is the
 * backend rejecting a token the app still held, which nobody asked for.
 *
 * The distinction exists so a Mini App can sign itself back in after a
 * rejected token without also undoing a sign-out the user meant.
 */
export type SessionEndReason = 'signed-out' | 'unauthorized';

/**
 * Why the session ended, for this page's life only.
 *
 * Deliberately in memory rather than in the persisted store: it gates a retry
 * decision about what just happened here, and a reason rehydrated from an
 * earlier load would be answering a different question.
 */
let sessionEndReason: SessionEndReason | null = null;

/** How the current session ended, or null if none has ended this page load. */
export function lastSessionEndReason(): SessionEndReason | null {
  return sessionEndReason;
}

type AuthState = {
  user: AuthUser | null;
  token: string | null;
  sessionSource: AuthSessionSource | null;
  telegramId: string | null;
  blockedProviders: string[];
  // Set at login when the server reports the password is guess-first. Held in
  // the store rather than on the login page because the user is navigated away
  // immediately — the prompt has to outlive that screen.
  passwordWeak: boolean;
  isAuthed: () => boolean;
  setSession: (
    user: AuthUser,
    token: string,
    meta?: {
      source?: AuthSessionSource;
      telegramId?: string | null;
      blockedProviders?: string[];
      passwordWeak?: boolean;
    },
  ) => void;
  // Cleared only when the password is actually changed. Navigating to the change
  // form does not clear it: leaving without changing must bring the prompt back.
  clearPasswordWeak: () => void;
  /** Defaults to a deliberate sign-out; only the 401 handler says otherwise. */
  clear: (reason?: SessionEndReason) => void;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      sessionSource: null,
      telegramId: null,
      blockedProviders: [],
      passwordWeak: false,
      isAuthed: () => Boolean(get().token),
      setSession: (user, token, meta) => {
        // A live session has no end reason — this also stops a recovered
        // session from looking like it is still owed a retry.
        sessionEndReason = null;
        // Offline caching starts here and nowhere else. Ten places write a
        // session; gating on this one means no sign-in path can forget it,
        // and no signed-out visitor can trigger it. It is idempotent, so
        // the session refreshes that also land here cost nothing.
        enableOfflineCache();
        set({
          user,
          token,
          sessionSource: meta?.source ?? 'password',
          telegramId: meta?.telegramId ?? null,
          blockedProviders: meta?.blockedProviders ?? [],
          // PRESERVED when the caller says nothing, not defaulted to false.
          //
          // Ten places write the session; only the password login knows whether
          // the password is weak. Defaulting to false let any of the others —
          // a session refresh during withdrawal, binding an account — silently
          // dismiss the prompt without the password ever changing.
          //
          // An explicit value still wins, so signing in again with a strong
          // password clears a stale flag.
          passwordWeak: meta?.passwordWeak ?? get().passwordWeak,
        });
      },
      clearPasswordWeak: () => set({ passwordWeak: false }),
      clear: (reason = 'signed-out') => {
        sessionEndReason = reason;
        set({
          user: null,
          token: null,
          sessionSource: null,
          telegramId: null,
          blockedProviders: [],
          passwordWeak: false,
        });
      },
    }),
    { name: 'hg.auth' },
  ),
);

type ToastVariant = 'success' | 'error';

type ToastItem = { message: string; variant: ToastVariant; ms: number };

type ToastState = {
  message: string | null;
  variant: ToastVariant;
  queue: ToastItem[];
  show: (message: string, ms?: number, variant?: ToastVariant) => void;
  showError: (message: string, ms?: number) => void;
  hide: () => void;
};

let toastTimer: ReturnType<typeof setTimeout> | null = null;

// Gap between toasts: matches the 300ms slide-out transition + small buffer.
const TOAST_GAP_MS = 350;

function drainQueue(
  get: () => ToastState,
  set: (partial: Partial<ToastState>) => void,
) {
  set({ message: null });
  toastTimer = setTimeout(() => {
    const { queue } = get();
    if (queue.length === 0) return;
    const [next, ...rest] = queue;
    set({ message: next.message, variant: next.variant, queue: rest });
    toastTimer = setTimeout(() => drainQueue(get, set), next.ms);
  }, TOAST_GAP_MS);
}

export const useToastStore = create<ToastState>((set, get) => ({
  message: null,
  variant: 'success',
  queue: [],
  show: (message, ms = 2500, variant = 'success') => {
    if (get().message !== null) {
      set({ queue: [...get().queue, { message, variant, ms }] });
      return;
    }
    if (toastTimer) clearTimeout(toastTimer);
    set({ message, variant });
    toastTimer = setTimeout(() => drainQueue(get, set), ms);
  },
  showError: (message, ms = 2500) => {
    if (get().message !== null) {
      set({ queue: [...get().queue, { message, variant: 'error', ms }] });
      return;
    }
    if (toastTimer) clearTimeout(toastTimer);
    set({ message, variant: 'error' });
    toastTimer = setTimeout(() => drainQueue(get, set), ms);
  },
  hide: () => {
    if (toastTimer) clearTimeout(toastTimer);
    set({ message: null, queue: [] });
  },
}));

// Funnel every token-loss path through one cleanup. Drop server-cached
// responses, the per-user notification watermark, the persisted auth /
// notifications localStorage entries (so DevTools shows a clean slate
// rather than null-state carcasses), and any in-flight toast. Locale is
// kept on purpose — it's a device preference, not per-user state.
useAuthStore.subscribe((s, prev) => {
  if (!prev.token || s.token) return;
  queryClient.clear();
  useNotificationsStore.setState({ lastSeenAt: 0 });
  useNotificationsStore.persist.clearStorage();
  useAuthStore.persist.clearStorage();
  useToastStore.getState().hide();
});

export type SessionKickedPayload = { country: string; ip: string; login_time: string };

type SessionKickedState = {
  payload: SessionKickedPayload | null;
  setKicked: (p: SessionKickedPayload) => void;
  dismiss: () => void;
};

export const useSessionKickedStore = create<SessionKickedState>((set) => ({
  payload: null,
  setKicked: (payload) => set({ payload }),
  dismiss: () => set({ payload: null }),
}));

// Cross-tab sign-out: Zustand's persist middleware doesn't watch storage
// events by default, so a logout in tab A leaves tab B's in-memory token
// alive even though localStorage already shows it cleared. Mirror the
// change here so the subscriber above fires in tab B too and its TanStack
// cache / watermarks drop. Storage events don't fire in the originating
// tab, so this can't loop back on itself.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== 'hg.auth') return;
    let nextToken: string | null = null;
    if (e.newValue) {
      try {
        const parsed = JSON.parse(e.newValue) as { state?: { token?: string | null } };
        nextToken = parsed.state?.token ?? null;
      } catch {
        nextToken = null;
      }
    }
    if (!nextToken && useAuthStore.getState().token) {
      useAuthStore.getState().clear();
    }
  });
}
